/**
 * WHAT THIS SHOP WARRANTS, IN THIS SHOP'S OWN WORDS.
 *
 * Until 2026-09-18 the foot of every invoice this system sent read:
 *
 *   "Labor is warranted 90 days or 5,000 miles, whichever comes first.
 *    Parts carry the manufacturer's warranty..."
 *
 * written as a literal in server/desk/invoice-email.ts. That is
 * Supercanic's warranty. It went out on three invoices from Wrench
 * Services LLC and one from 24/7 Swift Garage Door Services, which is a
 * written warranty made in their name, by software, promising a garage
 * door customer 5,000 miles.
 *
 * ─── WHY IT IS FREE TEXT AND NOT FIELDS ────────────────────────────────
 *
 * The same argument trade.ts makes. Structured columns -- labour_days,
 * labour_miles, parts_years -- would force every trade into a vehicle
 * shape, and miles mean nothing to a locksmith or a garage door shop.
 * Supercanic's own terms do not fit the fields either: "engines and
 * transmissions carry the supplier's own term" is a range of somebody
 * else's warranty and there is no column for it.
 *
 * A warranty is a promise a business makes in its own words. This module
 * holds the rule about WHEN to print those words, and never writes any.
 *
 * ─── PURE, AND IN src/domain, FOR THE USUAL REASON ─────────────────────
 *
 * The estimate email, the invoice email and the customer portal all print
 * this paragraph, and two of the three are server side. One spelling, one
 * rule, one place -- the way partcondition.ts and whereitwasdone.ts
 * already are. No imports.
 */

/**
 * The paragraph to print, or null when the shop has not stated one.
 *
 * NULL IS NOT A WARRANTY, AND IT IS NOT THE ABSENCE OF ONE EITHER. It
 * means this shop has not told us its terms, so this document says
 * nothing about warranty. A missing paragraph is not a legal claim; a
 * borrowed one is, and that was the bug.
 *
 * There is deliberately no fallback parameter. A caller cannot pass "use
 * Supercanic's if the shop has none", because that is the defect with a
 * nicer interface.
 */
export function warrantyParagraph(terms: unknown): string | null {
  const s = String(terms ?? "").trim();
  return s.length ? s : null;
}

/**
 * Whether this shop has said anything at all about what it warrants.
 *
 * The settings screen asks with this, and so does the invoice screen when
 * it wants to mention that the invoice is about to go out silent on the
 * subject. It reports; it does not block. A shop is allowed to offer no
 * warranty, and plenty of trades do.
 */
export function hasStatedWarranty(terms: unknown): boolean {
  return warrantyParagraph(terms) !== null;
}

/**
 * The nudge shown on the settings screen, or null when there is nothing
 * to say.
 *
 * Deliberately not phrased as a compliance failure. Saying nothing about
 * warranty is legal and normal. What is worth telling the owner is that
 * the box is empty and their paperwork is therefore silent, because the
 * shops most likely to be surprised by that are the ones who joined after
 * the hardcoded paragraph was removed and never saw it disappear.
 */
export function warrantyPrompt(terms: unknown, shopName: string): string | null {
  if (hasStatedWarranty(terms)) return null;
  const who = String(shopName ?? "").trim() || "This shop";
  return `${who} has not stated a warranty, so estimates and invoices say `
    + `nothing about one. Write it the way you would say it to a customer.`;
}

/**
 * A warranty that runs on distance only makes sense if the thing moves.
 *
 * This is not validation and it never refuses a save -- a shop may write
 * whatever it likes about its own promise, and there are odometers on
 * forklifts. It is a question asked back at the person typing, once,
 * because "5,000 miles" in the box on a garage door shop's settings
 * screen is exactly how this bug happened the first time, and the second
 * time it will have been typed by a human rather than compiled in.
 *
 * `trade` is the account's trade. Only 'auto', 'headlight' and 'rv' work
 * on things with odometers -- a Class A's chassis warranty is in miles.
 */
export function mileageLooksWrongForTrade(
  terms: unknown, trade: string | null | undefined,
): boolean {
  const s = warrantyParagraph(terms);
  if (!s) return false;
  const t = String(trade ?? "auto").trim().toLowerCase();
  if (t === "auto" || t === "headlight" || t === "rv") return false;
  return /\b(miles?|mileage|odometer|km|kilometers?|kilometres?)\b/i.test(s);
}
